import React, { useContext, useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon, TextField } from '@vr/lib';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroller';
import MapaContext from '../context/Mapa';
import chunk from '../helpers/chunk';
import DistanceFilter from '../components/DistanceFilter';
import ResultItem from '../components/ResultItem';

const ResultsContainer = styled.div`
  overflow: hidden;
  padding: 15px;
  max-height: 100vh;
  @media (max-width: ${({ theme }) => theme.devices.mobile}) {
    max-height: 50vh;
    min-height: 45vh;
  }
`;

const SearchInfoContainer = styled.div`
  position: relative;
  > div {
    padding-top: 0;
  }
  button {
    background: transparent;
    border: none;
    font-size: 18px;
    color: #fff;
    position: absolute;
    right: 5px;
    bottom: 5px;
    cursor: pointer;
  }
`;

const ListaResultados = styled.div`
  overflow: auto;
  height: ${({ height }) => height};
  margin-top: 10px;
  list-style: none;
  padding-right: 15px;
  ::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  ::-webkit-scrollbar-button {
    width: 0px;
    height: 0px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.white};
    border: 0px none ${({ theme }) => theme.colors.white};
    border-radius: 50px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.white};
  }
  ::-webkit-scrollbar-thumb:active {
    background: ${({ theme }) => theme.colors.white};
  }
  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.2);
    border: 17px none ${({ theme }) => theme.colors.white};
    border-radius: 50px;
  }
  ::-webkit-scrollbar-track:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  ::-webkit-scrollbar-track:active {
    background: rgba(255, 255, 255, 0.2);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const FooterResultados = styled.div`
  padding-top: 15px;
  text-align: center;
  font-size: 12px;
  color: #fff;
`;

export default function Results({ paginacao }) {
  const [listaResultadosHeight, setListaResultadosHeight] = useState('auto');
  const [currentPage, setCurrentPage] = useState(0);
  const [visibleLocations, setVisibleLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const {
    locations,
    raio,
    setRaio,
    search,
    openBox,
    setShowResultados,
    findEstablishment,
    empresaSelecionada,
    setEmpresaSelecionada,
    setFullBox,
    companyName,
  } = useContext(MapaContext);
  const refHeaderResumo = useRef(null);
  const refFooterResumo = useRef(null);
  const refResultContainer = useRef(null);

  const totalResultados = useMemo(() => locations.length, [locations]);
  const resultadosByPaginas = useMemo(() => chunk(locations, paginacao), [
    locations,
    paginacao,
  ]);
  const totalPaginas = useMemo(() => resultadosByPaginas.length, [
    resultadosByPaginas,
  ]);

  function boxResultHeight() {
    if (
      refHeaderResumo.current &&
      refFooterResumo.current &&
      refResultContainer.current
    ) {
      const resumoHeight = refHeaderResumo.current.clientHeight;
      const footerHeight = refFooterResumo.current.clientHeight;

      const boxContainerElement = document.getElementById('box-container');
      const boxContainerHeight = boxContainerElement.clientHeight;

      setListaResultadosHeight(
        `calc(${boxContainerHeight}px - ${resumoHeight}px - ${footerHeight}px - 35px)`,
      );
    }
  }

  function onChangeRaio(value) {
    setRaio(value);

    if (findEstablishment) {
      findEstablishment({ ...search, raio: value });
    }
  }

  function handleResetSearch() {
    setShowResultados(false);
    setFullBox(false);
    setEmpresaSelecionada({});
  }

  function handleLoadMore() {
    if (isLoading) {
      return;
    }

    if (currentPage + 1 <= totalPaginas - 1) {
      setIsLoading(true);
      setCurrentPage(currentPage + 1);

      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  }

  function onScrollEstabelecimento(empresa) {
    const isVisible = visibleLocations.find(item => item.id === empresa.id);
    if (!isVisible) {
      const index = locations.findIndex(item => item.id === empresa.id);
      const pagePosition = Math.ceil((index + 1) / paginacao);

      let adicionarEstabelecimentos = [];

      for (let i = currentPage + 1; i < pagePosition - 1; i += 1) {
        adicionarEstabelecimentos = [
          ...adicionarEstabelecimentos,
          ...resultadosByPaginas[i],
        ];
      }

      setVisibleLocations([...visibleLocations, ...adicionarEstabelecimentos]);
      setCurrentPage(pagePosition - 1);
    }

    setTimeout(() => {
      const element = document.getElementById(`estabelecimento-${empresa.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  }

  useEffect(boxResultHeight, [
    refHeaderResumo,
    refFooterResumo,
    refResultContainer,
  ]);

  useEffect(() => {
    setEmpresaSelecionada({});
    setCurrentPage(0);
  }, [locations, setEmpresaSelecionada]);

  useEffect(() => {
    if (currentPage === 0 && resultadosByPaginas[0]) {
      setVisibleLocations(resultadosByPaginas[0]);
    } else if (resultadosByPaginas[currentPage]) {
      setVisibleLocations(state => [
        ...state,
        ...resultadosByPaginas[currentPage],
      ]);
    } else if (totalPaginas === 0) {
      setVisibleLocations([]);
    }
  }, [totalPaginas, currentPage, resultadosByPaginas]);

  useEffect(() => {
    if (empresaSelecionada.origem === 'mapa') {
      onScrollEstabelecimento(empresaSelecionada);
    }
    // eslint-disable-next-line
  }, [empresaSelecionada]);

  return (
    <ResultsContainer open={openBox} ref={refResultContainer}>
      <div ref={refHeaderResumo}>
        <SearchInfoContainer>
          <TextField
            readOnly
            variant="light"
            defaultValue={search.referencePoint}
          />
          <button type="button" onClick={handleResetSearch}>
            <FontAwesomeIcon icon={['fas', 'times']} />
          </button>
        </SearchInfoContainer>

        <DistanceFilter
          value={raio}
          distances={[1, 3, 5]}
          onSelect={onChangeRaio}
        />
      </div>

      <ListaResultados id="lista-resultados" height={listaResultadosHeight}>
        <InfiniteScroll
          pageStart={0}
          loadMore={handleLoadMore}
          useWindow={false}
          hasMore={currentPage - 1 !== totalPaginas}
        >
          {visibleLocations.map(item => (
            <ResultItem
              key={item.id}
              item={item}
              onClick={() =>
                setEmpresaSelecionada({ ...item, origem: 'lista' })
              }
              active={empresaSelecionada.id === item.id}
              companyName={companyName}
            />
          ))}
        </InfiniteScroll>
      </ListaResultados>

      <FooterResultados ref={refFooterResumo}>
        {visibleLocations.length} resultados de {totalResultados}
      </FooterResultados>
    </ResultsContainer>
  );
}

Results.propTypes = {
  paginacao: PropTypes.number,
};

Results.defaultProps = {
  paginacao: 10,
};
